import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../assets/styles/FormView.css';
import GDPRConsent from '../pages/GDPRConsent';
// Import icons
import { 
  FaBuilding, FaChartLine, FaDollarSign, FaPercentage, 
  FaUsers, FaRegLightbulb, FaClipboardList, FaCheck,
  FaInfoCircle, FaArrowRight, FaSpinner, FaLeaf,
  FaMoneyBillWave, FaChartBar, FaShieldAlt
} from 'react-icons/fa';

const FormView = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [userResponses, setUserResponses] = useState({});
  const [initiativeName, setInitiativeName] = useState('');
  const [sector, setSector] = useState('');
  const [funding, setFunding] = useState('');
  const [currentMrr, setCurrentMrr] = useState('');
  const [previousMrr, setPreviousMrr] = useState('');
  const [burnRate, setBurnRate] = useState('');
  const [churnRate, setChurnRate] = useState('');
  const [ltm, setLtm] = useState('');
  const [ltv, setLtv] = useState('');
  const [cac, setCac] = useState('');
  const [stage, setStage] = useState('');
  const [marketCondition, setMarketCondition] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [gdprConsent, setGdprConsent] = useState(false); // New state for GDPR consent
  const formRef = useRef(null);

  const sectorOptions = [
    { value: 'saas', label: 'SaaS' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'deeptech', label: 'Deep Tech' },
    { value: 'marketplace', label: 'Marketplace' },
    { value: 'fintech', label: 'Fintech' },
    { value: 'healthtech', label: 'Healthtech' },
    { value: 'biotech', label: 'Biotech' },
    { value: 'aiml', label: 'AI/ML' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'other', label: 'Other' }
  ];
   
  const stageOptions = [
    { value: 'preSeed', label: 'Pre-Seed' },
    { value: 'seed', label: 'Seed' },
    { value: 'seriesA', label: 'Series A' },
    { value: 'seriesB', label: 'Series B' },
    { value: 'seriesC', label: 'Series C' },
    { value: 'seriesD', label: 'Series D' },
    { value: 'preIPO', label: 'Pre-IPO' }
  ];

  const marketConditionOptions = [
    { value: 'bull', label: 'Bull Market' },
    { value: 'neutral', label: 'Neutral Market' },
    { value: 'bear', label: 'Bear Market' }
  ];

  // Tool tips for financial metrics
  const tooltips = {
    mrr: "Monthly Recurring Revenue - The predictable revenue stream your business generates each month",
    burnRate: "The rate at which your company spends its cash reserves",
    churnRate: "The percentage of customers who stop using your product/service in a given period",
    ltm: "Last Twelve Months - Total revenue generated over the past 12 months",
    ltv: "Lifetime Value - The total revenue a business can expect from a single customer account",
    cac: "Customer Acquisition Cost - The cost of acquiring a new customer"
  };
   
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`https://api.startupdriver.co/api/forms/${id}`);
        setForm(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching form data:', error);
        setLoading(false);
      }
    };

    fetchForm();
  }, [id]);

  // Calculate completion percentage
  useEffect(() => {
    if (form) {
      // Count all required fields
      const totalRequiredFields = 12 + form.questions.length + 1; // Basic fields + questions + GDPR consent
      
      // Count completed fields
      let completedFields = 0;
      
      if (initiativeName) completedFields++;
      if (sector) completedFields++;
      if (stage) completedFields++;
      if (marketCondition) completedFields++;
      if (funding) completedFields++;
      if (currentMrr) completedFields++;
      if (previousMrr) completedFields++;
      if (burnRate) completedFields++;
      if (churnRate) completedFields++;
      if (ltm) completedFields++;
      if (ltv) completedFields++;
      if (cac) completedFields++;
      if (gdprConsent) completedFields++; // Add GDPR consent to completion calculation
      
      // Add answered questions
      completedFields += Object.keys(userResponses).length;
      
      const percentage = Math.round((completedFields / totalRequiredFields) * 100);
      setCompletionPercentage(percentage);
    }
  }, [form, initiativeName, sector, stage, marketCondition, funding, currentMrr, 
      previousMrr, burnRate, churnRate, ltm, ltv, cac, userResponses, gdprConsent]);

  const handleCheckboxChange = (qIndex, aIndex) => {
    const questionId = form.questions[qIndex]._id;
    const selectedAnswer = form.questions[qIndex].answers[aIndex].answerText;

    setUserResponses((prev) => ({
      ...prev,
      [questionId]: selectedAnswer,
    }));
  };

  const handleNumberInput = (value, setter) => {
    if (/^\d*$/.test(value)) {
      setter(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for GDPR consent before submission
    if (!gdprConsent) {
      alert('Please accept the Privacy Policy to continue.');
      return;
    }
    
    // Show loading state
    setLoading(true);

    try {
      await axios.post('https://api.startupdriver.co/api/responses', {
        formId: id,
        initiativeName,
        sector,
        stage,
        marketCondition,
        funding,
        currentMrr,
        previousMrr,
        burnRate,
        churnRate,
        ltm,
        ltv,
        cac,
        userResponses: Object.entries(userResponses).map(([questionId, selectedAnswer]) => ({
          questionId,
          selectedAnswer,
        })),
        gdprConsent: true, // Include GDPR consent in the submission
      });
      setSubmitSuccess(true);
      setLoading(false);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Also focus on the success message for accessibility
      if (formRef.current) {
        const successMessage = formRef.current.querySelector('.success-message');
        if (successMessage) {
          successMessage.focus();
        }
      }
    } catch (error) {
      console.error('Error saving responses:', error);
      setSubmitSuccess(false);
      setLoading(false);
      
      // Also scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading && !form) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div>Loading your form...</div>
      </div>
    );
  }
  
  if (!form) return (
    <div className="form-view">
      <div className="error-message">
        <FaInfoCircle /> Error loading form data. Please try again later.
      </div>
    </div>
  );

  return (
    <div className="form-view" ref={formRef}>
      {/* Progress bar */}
      <div className="form-progress">
        <div className="progress-bar" style={{ width: `${completionPercentage}%` }}></div>
      </div>
      
      {/* Completion status */}
      <div className="completion-meter">
        <div className="completion-icon">
          <FaClipboardList />
        </div>
        <div className="completion-text">
          <div className="completion-title">Your progress</div>
          <div className="completion-subtitle">Complete the form to submit your initiative</div>
        </div>
        <div className="completion-progress">
          <div className="completion-bar" style={{ width: `${completionPercentage}%` }}></div>
        </div>
        <div className="form-badge">{completionPercentage}% Complete</div>
      </div>
      
      {submitSuccess === true && (
        <div className="success-message" tabIndex="-1">
          <FaCheck /> Your responses have been submitted successfully!
        </div>
      )}
      
      {submitSuccess === false && (
        <div className="error-message" tabIndex="-1">
          <FaInfoCircle /> There was an error submitting your responses. Please try again.
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Basic Info Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon"><FaLeaf /></div>
            <h2 className="section-title">Initiative Information</h2>
          </div>
          
          <div className="initiative-info">
            <div className="form-group">
              <label htmlFor="initiativeName">Initiative Name</label>
              <div className="input-with-icon">
                <FaBuilding className="input-icon" />
                <input
                  id="initiativeName"
                  type="text"
                  value={initiativeName}
                  onChange={(e) => setInitiativeName(e.target.value)}
                  placeholder="Enter your initiative name"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="sector">Sector</label>
              <div className="input-with-icon">
                <FaRegLightbulb className="input-icon" />
                <select 
                  id="sector"
                  value={sector} 
                  onChange={(e) => setSector(e.target.value)}
                  required
                  className="form-select"
                >
                  <option value="">Select a sector</option>
                  {sectorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="stage">Development Stage</label>
              <div className="input-with-icon">
                <FaChartLine className="input-icon" />
                <select 
                  id="stage"
                  value={stage} 
                  onChange={(e) => setStage(e.target.value)}
                  required
                  className="form-select"
                >
                  <option value="">Select a stage</option>
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="marketCondition">Market Condition</label>
              <div className="input-with-icon">
                <FaChartLine className="input-icon" />
                <select 
                  id="marketCondition"
                  value={marketCondition} 
                  onChange={(e) => setMarketCondition(e.target.value)}
                  required
                  className="form-select"
                >
                  <option value="">Select market condition</option>
                  {marketConditionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        {/* Financial Metrics Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon"><FaMoneyBillWave /></div>
            <h2 className="section-title">Financial Metrics</h2>
          </div>
          
          <div className="initiative-info">
            <div className="form-group">
              <label htmlFor="funding">
                Total Investment ($)
              </label>
              <div className="input-with-icon">
                <FaDollarSign className="input-icon" />
                <input
                  id="funding"
                  type="text"
                  value={funding}
                  onChange={(e) => handleNumberInput(e.target.value, setFunding)}
                  placeholder="Enter amount in USD"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="currentMrr">
                Current Month's MRR ($)
                <div className="tooltip">
                  <div className="tooltip-icon">i</div>
                  <span className="tooltip-text">{tooltips.mrr}</span>
                </div>
              </label>
              <div className="input-with-icon">
                <FaDollarSign className="input-icon" />
                <input
                  id="currentMrr"
                  type="text"
                  value={currentMrr}
                  onChange={(e) => handleNumberInput(e.target.value, setCurrentMrr)}
                  placeholder="Enter amount in USD"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="previousMrr">Previous Month's MRR ($)</label>
              <div className="input-with-icon">
                <FaDollarSign className="input-icon" />
                <input
                  id="previousMrr"
                  type="text"
                  value={previousMrr}
                  onChange={(e) => handleNumberInput(e.target.value, setPreviousMrr)}
                  placeholder="Enter amount in USD"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="burnRate">
                Monthly Burn Rate (%)
                <div className="tooltip">
                  <div className="tooltip-icon">i</div>
                  <span className="tooltip-text">{tooltips.burnRate}</span>
                </div>
              </label>
              <div className="input-with-icon">
                <FaPercentage className="input-icon" />
                <input
                  id="burnRate"
                  type="text"
                  value={burnRate}
                  onChange={(e) => handleNumberInput(e.target.value, setBurnRate)}
                  placeholder="Enter percentage"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="churnRate">
                Monthly Churn Rate (%)
                <div className="tooltip">
                  <div className="tooltip-icon">i</div>
                  <span className="tooltip-text">{tooltips.churnRate}</span>
                </div>
              </label>
              <div className="input-with-icon">
                <FaPercentage className="input-icon" />
                <input
                  id="churnRate"
                  type="text"
                  value={churnRate}
                  onChange={(e) => handleNumberInput(e.target.value, setChurnRate)}
                  placeholder="Enter percentage"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="ltm">
                LTM ($)
                <div className="tooltip">
                  <div className="tooltip-icon">i</div>
                  <span className="tooltip-text">{tooltips.ltm}</span>
                </div>
              </label>
              <div className="input-with-icon">
                <FaDollarSign className="input-icon" />
                <input
                  id="ltm"
                  type="text"
                  value={ltm}
                  onChange={(e) => handleNumberInput(e.target.value, setLtm)}
                  placeholder="Enter amount in USD"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="ltv">
                LTV ($)
                <div className="tooltip">
                  <div className="tooltip-icon">i</div>
                  <span className="tooltip-text">{tooltips.ltv}</span>
                </div>
              </label>
              <div className="input-with-icon">
                <FaDollarSign className="input-icon" />
                <input
                  id="ltv"
                  type="text"
                  value={ltv}
                  onChange={(e) => handleNumberInput(e.target.value, setLtv)}
                  placeholder="Enter amount in USD"
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="cac">
                CAC ($)
                <div className="tooltip">
                  <div className="tooltip-icon">i</div>
                  <span className="tooltip-text">{tooltips.cac}</span>
                </div>
              </label>
              <div className="input-with-icon">
                <FaDollarSign className="input-icon" />
                <input
                  id="cac"
                  type="text"
                  value={cac}
                  onChange={(e) => handleNumberInput(e.target.value, setCac)}
                  placeholder="Enter amount in USD"
                  required
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Questions Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon"><FaChartBar /></div>
            <h2 className="section-title">Assessment Questions</h2>
          </div>
          
          {form.questions.map((question, qIndex) => (
            <div key={qIndex} className="question-block">
              <h3>{question.questionText}</h3>
              <ul>
                {question.answers.map((answer, aIndex) => (
                  <li key={aIndex}>
                    <div className="radio-button">
                      <input
                        type="radio"
                        name={`question${qIndex}`}
                        id={`q${qIndex}a${aIndex}`}
                        onChange={() => handleCheckboxChange(qIndex, aIndex)}
                        required
                      />
                      <label htmlFor={`q${qIndex}a${aIndex}`} className="radio-control"></label>
                    </div>
                    <label htmlFor={`q${qIndex}a${aIndex}`} className="answer-text">
                      {answer.answerText}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* GDPR Consent Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon"><FaShieldAlt /></div>
            <h2 className="section-title">Data Protection</h2>
          </div>
          
          {/* Add GDPR Consent Component */}
          <GDPRConsent onConsentChange={(isChecked) => setGdprConsent(isChecked)} />
        </div>

        <div className="button-area">
          {loading && form ? (
            <button type="button" disabled className="formViewLoading">
              <FaSpinner className="formViewSpinner" style={{ marginRight: '8px' }} />
              Submitting...
            </button>
          ) : submitSuccess ? (
            <button type="button" disabled className="formViewSuccess">
              <FaCheck style={{ marginRight: '8px' }} />
              Form Submitted
            </button>
          ) : (
            <button 
              className='formViewSubmit' 
              type="submit"
              disabled={!gdprConsent}
            >
              Submit Responses
              <FaArrowRight style={{ marginLeft: '8px' }} />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default FormView;
